import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button, formatShort } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import {
  fetchExpenseCategories,
  fetchExpenseSeries,
  homeExpenseQueryKeys,
  patchExpenseSeries,
} from "@apps/dashboard/lib/home-expense-api";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";

function seriesStatusBadgeKey(
  status: "active" | "paused" | "ended",
): "seriesStatusBadgeActive" | "seriesStatusBadgePaused" | "seriesStatusBadgeEnded" {
  if (status === "paused") return "seriesStatusBadgePaused";
  if (status === "ended") return "seriesStatusBadgeEnded";
  return "seriesStatusBadgeActive";
}

export default function ExpenseSeriesDetailPage(): JSX.Element {
  const { houseId, seriesId } = useParams<{ houseId: string; seriesId: string }>();
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  useSyncHouseFromParams(houseId);

  const [amountInput, setAmountInput] = useState("");
  const [endsOnInput, setEndsOnInput] = useState("");
  const [categoryIdInput, setCategoryIdInput] = useState("");
  const [amountFormError, setAmountFormError] = useState<string | null>(null);
  const [endsOnFormError, setEndsOnFormError] = useState<string | null>(null);
  const [endSeriesError, setEndSeriesError] = useState<string | null>(null);
  const [extendError, setExtendError] = useState<string | null>(null);

  const seriesQuery = useQuery({
    queryKey:
      houseId && seriesId
        ? homeExpenseQueryKeys.expenseSeries(locale, houseId, seriesId)
        : ["expense-series", "noop"],
    queryFn: () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      return fetchExpenseSeries(locale, houseId, seriesId);
    },
    enabled: Boolean(houseId && seriesId),
  });

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing house");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const invalidateSeriesRelated = async (): Promise<void> => {
    if (!houseId || !seriesId) return;
    await queryClient.invalidateQueries({
      queryKey: homeExpenseQueryKeys.expenseSeries(locale, houseId, seriesId),
    });
    await queryClient.invalidateQueries({
      queryKey: homeExpenseQueryKeys.expenseSeriesList(locale, houseId),
    });
    await queryClient.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
  };

  useEffect(() => {
    const data = seriesQuery.data;
    if (!data) return;
    setAmountInput(String(data.installmentAmountMinor / 100).replace(".", ","));
    setCategoryIdInput(data.categoryId ?? "");
    if (data.endsOn) {
      setEndsOnInput(data.endsOn);
    } else {
      setEndsOnInput("");
    }
  }, [seriesQuery.data]);

  const patchMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      const minor = parseAmountToMinor(amountInput.trim());
      if (minor === null || minor < 0) {
        throw new Error("invalid_amount");
      }
      return patchExpenseSeries(locale, houseId, seriesId, {
        installmentAmountMinor: minor,
        categoryId: categoryIdInput || null,
      });
    },
    onSuccess: async () => {
      setAmountFormError(null);
      await invalidateSeriesRelated();
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message === "invalid_amount") {
        setAmountFormError(t("dashboardUi.expenses.form.invalidAmount"));
        return;
      }
      setAmountFormError(
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic"),
      );
    },
  });

  const endsOnMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      const trimmed = endsOnInput.trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        throw new Error("invalid_end_date");
      }
      return patchExpenseSeries(locale, houseId, seriesId, { endsOn: trimmed });
    },
    onSuccess: async () => {
      setEndsOnFormError(null);
      await invalidateSeriesRelated();
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message === "invalid_end_date") {
        setEndsOnFormError(t("dashboardUi.expenses.detail.seriesInvalidEndDate"));
        return;
      }
      setEndsOnFormError(
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic"),
      );
    },
  });

  const endSeriesMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      return patchExpenseSeries(locale, houseId, seriesId, { status: "ended" });
    },
    onSuccess: async () => {
      setEndSeriesError(null);
      await invalidateSeriesRelated();
    },
    onError: (err: unknown) => {
      setEndSeriesError(
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic"),
      );
    },
  });

  const clearEndsOnMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      return patchExpenseSeries(locale, houseId, seriesId, { endsOn: null });
    },
    onSuccess: async () => {
      setExtendError(null);
      await invalidateSeriesRelated();
    },
    onError: (err: unknown) => {
      setExtendError(
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic"),
      );
    },
  });

  const reactivateOpenEndedMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      return patchExpenseSeries(locale, houseId, seriesId, {
        status: "active",
        endsOn: null,
      });
    },
    onSuccess: async () => {
      setExtendError(null);
      await invalidateSeriesRelated();
    },
    onError: (err: unknown) => {
      setExtendError(
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic"),
      );
    },
  });

  const reactivateOnlyMutation = useMutation({
    mutationFn: async () => {
      if (!houseId || !seriesId) throw new Error("missing params");
      return patchExpenseSeries(locale, houseId, seriesId, { status: "active" });
    },
    onSuccess: async () => {
      setExtendError(null);
      await invalidateSeriesRelated();
    },
    onError: (err: unknown) => {
      setExtendError(
        err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic"),
      );
    },
  });

  if (!houseId || !seriesId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (seriesQuery.isLoading) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <LoadingState />
      </div>
    );
  }

  if (seriesQuery.isError || !seriesQuery.data) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
          {t("dashboardUi.expenses.backToJournal")}
        </DashboardBackLink>
        <ErrorState onRetry={() => void seriesQuery.refetch()} />
      </div>
    );
  }

  const s = seriesQuery.data;
  const isEnded = s.status === "ended";
  const statusBadge = t(`dashboardUi.expenses.detail.${seriesStatusBadgeKey(s.status)}`);
  const hasEndsOn = Boolean(s.endsOn);
  const showLimitSection = !isEnded;
  const showExtendActive = !isEnded && hasEndsOn;
  const showExtendEndedWithEnd = isEnded && hasEndsOn;
  const showExtendEndedNoEnd = isEnded && !hasEndsOn;
  const showExtendSection = showExtendActive || showExtendEndedWithEnd || showExtendEndedNoEnd;
  const seriesBusy =
    patchMutation.isPending ||
    endsOnMutation.isPending ||
    endSeriesMutation.isPending ||
    clearEndsOnMutation.isPending ||
    reactivateOpenEndedMutation.isPending ||
    reactivateOnlyMutation.isPending;

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
        {t("dashboardUi.expenses.backToJournal")}
      </DashboardBackLink>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="font-headline text-headline-sm font-bold text-primary">{s.title}</h1>
        <span className="rounded-full bg-surface-container-high px-3 py-1 text-label-sm text-on-surface-variant">
          {statusBadge}
        </span>
      </div>

      <div className="mt-8 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15">
        <p className="text-label-md text-on-surface-variant">
          {formatMinorAsCurrency(s.installmentAmountMinor, s.currency, locale)} · {s.frequency}
        </p>
        {s.endsOn ? (
          <p className="mt-2 text-body-md text-on-surface-variant">
            {formatShort(s.endsOn)}
          </p>
        ) : null}
      </div>

      {isEnded ? (
        <p className="mt-6 text-body-md text-on-surface-variant">
          {t("dashboardUi.expenses.detail.seriesEndedReadOnly")}
        </p>
      ) : null}

      <form
        className="mt-8 space-y-4"
        onSubmit={(ev) => {
          ev.preventDefault();
          void patchMutation.mutateAsync();
        }}
      >
        <div>
          <label htmlFor="series-amt" className="text-label-md text-on-surface-variant">
            {t("dashboardUi.expenses.detail.seriesFutureInstallmentLabel")}
          </label>
          <input
            id="series-amt"
            type="text"
            inputMode="decimal"
            disabled={isEnded || seriesBusy}
            className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary disabled:opacity-50"
            placeholder={t("dashboardUi.expenses.form.amountHint")}
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="series-category" className="text-label-md text-on-surface-variant">
            {t("dashboardUi.expenses.form.categoryLabel")}
          </label>
          <select
            id="series-category"
            value={categoryIdInput}
            disabled={isEnded || seriesBusy || categoriesQuery.isLoading}
            onChange={(e) => setCategoryIdInput(e.target.value)}
            className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary disabled:opacity-50"
          >
            <option value="">{t("dashboardUi.expenses.form.categoryNone")}</option>
            {(categoriesQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {amountFormError ? <p className="text-body-md text-error">{amountFormError}</p> : null}
        <Button type="submit" disabled={patchMutation.isPending || isEnded || seriesBusy}>
          {patchMutation.isPending ? t("dashboardUi.expenses.form.saving") : t("dashboardUi.expenses.detail.seriesSave")}
        </Button>
      </form>

      {showLimitSection ? (
        <div className="mt-10 space-y-4 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15">
          <h2 className="font-headline text-title-md font-semibold text-primary">
            {t("dashboardUi.expenses.detail.seriesLimitSectionTitle")}
          </h2>
          <p className="text-body-sm text-on-surface-variant">
            {t("dashboardUi.expenses.detail.seriesLimitSectionIntro")}
          </p>
          <p className="text-body-sm text-on-surface-variant">
            {t("dashboardUi.expenses.detail.seriesEndsOnHelp")}
          </p>
          <div>
            <label htmlFor="series-ends-on" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.detail.seriesEndsOnLabel")}
            </label>
            <input
              id="series-ends-on"
              type="date"
              disabled={seriesBusy}
              className="mt-1 w-full max-w-xs rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary disabled:opacity-50"
              value={endsOnInput}
              onChange={(e) => setEndsOnInput(e.target.value)}
            />
          </div>
          {endsOnFormError ? <p className="text-body-md text-error">{endsOnFormError}</p> : null}
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              disabled={seriesBusy}
              onClick={() => void endsOnMutation.mutateAsync()}
            >
              {endsOnMutation.isPending
                ? t("dashboardUi.expenses.form.saving")
                : t("dashboardUi.expenses.detail.seriesApplyEndsOn")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={seriesBusy}
              onClick={() => {
                if (!window.confirm(t("dashboardUi.expenses.detail.seriesEndNowConfirm"))) {
                  return;
                }
                void endSeriesMutation.mutateAsync();
              }}
            >
              {endSeriesMutation.isPending
                ? t("dashboardUi.expenses.form.saving")
                : t("dashboardUi.expenses.detail.seriesEndNow")}
            </Button>
          </div>
          {endSeriesError ? <p className="text-body-md text-error">{endSeriesError}</p> : null}
        </div>
      ) : null}

      {showExtendSection ? (
        <div className="mt-10 space-y-4 rounded-2xl border-2 border-dashed border-primary/30 bg-surface-container-low p-6">
          <h2 className="font-headline text-title-md font-semibold text-primary">
            {t("dashboardUi.expenses.detail.seriesExtendSectionTitle")}
          </h2>
          {showExtendActive ? (
            <p className="text-body-sm text-on-surface-variant">
              {t("dashboardUi.expenses.detail.seriesExtendIntroActiveWithEnd")}
            </p>
          ) : null}
          {showExtendEndedWithEnd ? (
            <p className="text-body-sm text-on-surface-variant">
              {t("dashboardUi.expenses.detail.seriesExtendIntroEnded")}
            </p>
          ) : null}
          {showExtendEndedNoEnd ? (
            <p className="text-body-sm text-on-surface-variant">
              {t("dashboardUi.expenses.detail.seriesExtendIntroEndedNoEnd")}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {showExtendActive ? (
              <Button
                type="button"
                variant="secondary"
                disabled={seriesBusy}
                onClick={() => {
                  if (!window.confirm(t("dashboardUi.expenses.detail.seriesRemoveEndsOnConfirm"))) {
                    return;
                  }
                  void clearEndsOnMutation.mutateAsync();
                }}
              >
                {clearEndsOnMutation.isPending
                  ? t("dashboardUi.expenses.form.saving")
                  : t("dashboardUi.expenses.detail.seriesRemoveEndsOn")}
              </Button>
            ) : null}
            {showExtendEndedWithEnd ? (
              <Button
                type="button"
                variant="secondary"
                disabled={seriesBusy}
                onClick={() => {
                  if (
                    !window.confirm(
                      t("dashboardUi.expenses.detail.seriesReactivateOpenEndedConfirm"),
                    )
                  ) {
                    return;
                  }
                  void reactivateOpenEndedMutation.mutateAsync();
                }}
              >
                {reactivateOpenEndedMutation.isPending
                  ? t("dashboardUi.expenses.form.saving")
                  : t("dashboardUi.expenses.detail.seriesReactivateOpenEnded")}
              </Button>
            ) : null}
            {showExtendEndedNoEnd ? (
              <Button
                type="button"
                variant="secondary"
                disabled={seriesBusy}
                onClick={() => {
                  if (!window.confirm(t("dashboardUi.expenses.detail.seriesReactivateOnlyConfirm"))) {
                    return;
                  }
                  void reactivateOnlyMutation.mutateAsync();
                }}
              >
                {reactivateOnlyMutation.isPending
                  ? t("dashboardUi.expenses.form.saving")
                  : t("dashboardUi.expenses.detail.seriesReactivateOnly")}
              </Button>
            ) : null}
          </div>
          {extendError ? <p className="text-body-md text-error">{extendError}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
